import React, { useState } from 'react';
import styles from './contactForm.module.css';

function formxSubmitUrl() {
  if (process.env.REACT_APP_FORMX_SUBMIT_URL) {
    return process.env.REACT_APP_FORMX_SUBMIT_URL;
  }
  const token = process.env.REACT_APP_FORMX_TOKEN;
  if (token) {
    return `https://api.timetofuture.com/api/forms/public/${token}/submit`;
  }
  return '';
}

/**
 * Contact form backed by Formx (https://formx.timetofuture.com/docs).
 * compact: name + phone + message (home/landing embed)
 * full: name, email, phone, message (contact page)
 */
const ContactForm = ({ compact = false, idPrefix = 'contact' }) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    if (data.get('_gotcha')) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = {
      name: String(data.get('name') || '').trim(),
      email: String(data.get('email') || '').trim(),
      phone: String(data.get('phone') || '').trim(),
      message: String(data.get('message') || '').trim(),
    };

    if (!payload.name || !payload.phone) {
      setStatus('error');
      setError('Please enter your name and phone number.');
      return;
    }
    if (!compact && !payload.email) {
      setStatus('error');
      setError('Please enter your email address.');
      return;
    }
    if (!payload.message) {
      payload.message = compact ? 'Callback request from the website.' : '';
    }
    if (!compact && !payload.message) {
      setStatus('error');
      setError('Please enter a message.');
      return;
    }

    const url = formxSubmitUrl();
    if (!url) {
      setStatus('error');
      setError(
        'Contact form is not configured. Add REACT_APP_FORMX_TOKEN or REACT_APP_FORMX_SUBMIT_URL.'
      );
      return;
    }

    setStatus('sending');
    setError('');

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.message || body.error || 'Could not send message.');
      }
      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      setError(err.message || 'Could not send message. Please try again.');
    }
  };

  return (
    <form
      className={`${styles.form} ${compact ? '' : styles.formFull}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${idPrefix}-name`}>
          Full name
        </label>
        <input
          className={styles.input}
          id={`${idPrefix}-name`}
          name="name"
          type="text"
          autoComplete="name"
          required
        />
      </div>

      {!compact ? (
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${idPrefix}-email`}>
            Email
          </label>
          <input
            className={styles.input}
            id={`${idPrefix}-email`}
            name="email"
            type="email"
            autoComplete="email"
            required
          />
        </div>
      ) : null}

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${idPrefix}-phone`}>
          Phone
        </label>
        <input
          className={styles.input}
          id={`${idPrefix}-phone`}
          name="phone"
          type="tel"
          autoComplete="tel"
          required
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${idPrefix}-message`}>
          {compact ? 'Message (optional)' : 'Message'}
        </label>
        <textarea
          className={styles.textarea}
          id={`${idPrefix}-message`}
          name="message"
          rows={compact ? 3 : 5}
          required={!compact}
        />
      </div>

      <input
        className={styles.honeypot}
        type="text"
        name="_gotcha"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <button className={styles.submit} type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : compact ? 'Send request' : 'Send message'}
      </button>

      {status === 'success' ? (
        <p className={`${styles.status} ${styles.success}`} role="status">
          Thanks — we received your message and will get back to you shortly.
        </p>
      ) : null}
      {status === 'error' ? (
        <p className={`${styles.status} ${styles.error}`} role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
};

export default ContactForm;
